import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './WorkflowBuilder.css';

function WorkflowBuilder({ workflow, onChange }) {
  const [showAddMenu, setShowAddMenu] = useState(null); // { nodeId, type: 'action' | 'yes' | 'no' }
  const [selectedNode, setSelectedNode] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const [uploadingNodeId, setUploadingNodeId] = useState(null);
  const [imageUploadError, setImageUploadError] = useState(null);
  const imageInputRef = useRef(null);
  const selectedNodeRef = useRef(null);
  
  // Initialize with start node if empty
  const initialNodes = workflow?.nodes || [
    { 
      id: 'start', 
      type: 'start', 
      label: 'Campaign Start',
      icon: '🚀',
      data: {},
      children: []
    }
  ];
  
  const [nodes, setNodes] = useState(initialNodes);

  useEffect(() => {
    selectedNodeRef.current = selectedNode;
  }, [selectedNode]);

  // Action types
  const actionTypes = [
    { type: 'connection_request', label: 'Send Connection Request', icon: '🤝' },
    { type: 'send_message', label: 'Send Message', icon: '💬' },
    { type: 'inmail', label: 'Send InMail', icon: '📧' },
    { type: 'view_profile', label: 'View Profile', icon: '👁️' },
    { type: 'follow', label: 'Follow', icon: '➕' },
    { type: 'delay', label: 'Add Delay', icon: '⏰' },
    { type: 'connection_request_condition', label: 'If Connection Accepted', icon: '🔀' },
    { type: 'message_condition', label: 'If Message Replied', icon: '💭' },
    { type: 'first_connection_condition', label: 'If First Connection', icon: '🔗' },
    { type: 'end', label: 'End', icon: '✓' }
  ];

  // Find node by ID recursively
  const findNode = (nodeList, nodeId) => {
    for (const node of nodeList) {
      if (node.id === nodeId) return node;
      if (node.yesChild) {
        const found = findNode([node.yesChild], nodeId);
        if (found) return found;
      }
      if (node.noChild) {
        const found = findNode([node.noChild], nodeId);
        if (found) return found;
      }
      if (node.child) {
        const found = findNode([node.child], nodeId);
        if (found) return found;
      }
    }
    return null;
  };

  // Update node in tree recursively
  const updateNodeInTree = (nodeList, nodeId, updates) => {
    return nodeList.map(node => {
      if (node.id === nodeId) {
        return { ...node, ...updates };
      }
      
      // 需要检查所有子分支，而不是只检查第一个
      let updatedNode = { ...node };
      let hasUpdate = false;
      
      // 检查 yesChild
      if (node.yesChild) {
        const result = updateNodeInTree([node.yesChild], nodeId, updates);
        if (result[0] !== node.yesChild) {
          updatedNode.yesChild = result[0];
          hasUpdate = true;
        }
      }
      
      // 检查 noChild
      if (node.noChild) {
        const result = updateNodeInTree([node.noChild], nodeId, updates);
        if (result[0] !== node.noChild) {
          updatedNode.noChild = result[0];
          hasUpdate = true;
        }
      }
      
      // 检查 child
      if (node.child) {
        const result = updateNodeInTree([node.child], nodeId, updates);
        if (result[0] !== node.child) {
          updatedNode.child = result[0];
          hasUpdate = true;
        }
      }
      
      return hasUpdate ? updatedNode : node;
    });
  };

  // Add child node to specific branch
  const addNodeToTree = (nodeList, parentId, branchType, newNode) => {
    return nodeList.map(node => {
      if (node.id === parentId) {
        if (branchType === 'yes') {
          return { ...node, yesChild: newNode };
        } else if (branchType === 'no') {
          return { ...node, noChild: newNode };
        } else {
          return { ...node, child: newNode };
        }
      }
      // Recursively search in yesChild, noChild, and child
      // Need to check all children, not just the first one found
      let updatedNode = { ...node };
      let hasUpdate = false;
      
      // Check yesChild
      if (node.yesChild) {
        const result = addNodeToTree([node.yesChild], parentId, branchType, newNode);
        if (result[0] !== node.yesChild) {
          updatedNode.yesChild = result[0];
          hasUpdate = true;
        }
      }
      
      // Check noChild (even if yesChild was updated, we still need to check noChild)
      if (node.noChild) {
        const result = addNodeToTree([node.noChild], parentId, branchType, newNode);
        if (result[0] !== node.noChild) {
          updatedNode.noChild = result[0];
          hasUpdate = true;
        }
      }
      
      // Check child (even if yesChild or noChild was updated, we still need to check child)
      if (node.child) {
        const result = addNodeToTree([node.child], parentId, branchType, newNode);
        if (result[0] !== node.child) {
          updatedNode.child = result[0];
          hasUpdate = true;
        }
      }
      
      return hasUpdate ? updatedNode : node;
    });
  };

  // Add child node
  const addChildNode = (parentId, branchType, actionType) => {
    const newNode = {
      id: `node_${Date.now()}`,
      type: actionType.type,
      label: actionType.label,
      icon: actionType.icon,
      data: {}
    };

    // Special handling for condition nodes
    if (actionType.type === 'connection_request_condition' || actionType.type === 'message_condition' || actionType.type === 'first_connection_condition') {
      newNode.yesChild = null;
      newNode.noChild = null;
    } else if (actionType.type === 'end') {
      // End node has no children
      newNode.child = null;
    } else {
      newNode.child = null;
    }

    const updatedNodes = addNodeToTree(nodes, parentId, branchType, newNode);
    setNodes(updatedNodes);
    setShowAddMenu(null);
    notifyChange(updatedNodes);
  };

  // Delete node
  const deleteNode = (nodeId) => {
    if (nodeId === 'start') return;
    
    const removeNodeFromTree = (nodeList) => {
      return nodeList.map(node => {
        // 先检查直接子节点
        if (node.child && node.child.id === nodeId) {
          return { ...node, child: node.child.child || null };
        }
        if (node.yesChild && node.yesChild.id === nodeId) {
          return { ...node, yesChild: null };
        }
        if (node.noChild && node.noChild.id === nodeId) {
          return { ...node, noChild: null };
        }
        
        // 然后递归检查所有子分支
        let updatedNode = { ...node };
        let hasUpdate = false;
        
        if (node.child) {
          const result = removeNodeFromTree([node.child]);
          if (result[0] !== node.child) {
            updatedNode.child = result[0];
            hasUpdate = true;
          }
        }
        
        if (node.yesChild) {
          const result = removeNodeFromTree([node.yesChild]);
          if (result[0] !== node.yesChild) {
            updatedNode.yesChild = result[0];
            hasUpdate = true;
          }
        }
        
        if (node.noChild) {
          const result = removeNodeFromTree([node.noChild]);
          if (result[0] !== node.noChild) {
            updatedNode.noChild = result[0];
            hasUpdate = true;
          }
        }
        
        return hasUpdate ? updatedNode : node;
      });
    };

    const updatedNodes = removeNodeFromTree(nodes);
    setNodes(updatedNodes);
    setSelectedNode(null);
    notifyChange(updatedNodes);
  };

  // Update node data
  // Helper function to insert field at cursor position in textarea
  const insertFieldAtCursor = (field, nodeId) => {
    // Find the active textarea (the one that has focus or is in the selected node's config panel)
    const textarea = document.querySelector('textarea:focus') || 
                     document.querySelector('.node-config-panel textarea');
    
    if (textarea && selectedNode) {
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const currentMessage = selectedNode.data.message || '';
      const newMessage = currentMessage.slice(0, start) + field + currentMessage.slice(end);
      updateNodeData(nodeId, { message: newMessage });
      
      // Set cursor position after inserted field
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = start + field.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    } else if (selectedNode) {
      // Fallback: append to end
      const currentMessage = selectedNode.data.message || '';
      updateNodeData(nodeId, { message: currentMessage + field });
    }
  };

  const updateNodeData = (nodeId, data) => {
    const currentNode = findNode(nodes, nodeId);
    const updatedNodeData = { data: { ...currentNode?.data, ...data } };
    const updatedNodes = updateNodeInTree(nodes, nodeId, updatedNodeData);
    setNodes(updatedNodes);
    
    // Update selectedNode to trigger re-render
    if (selectedNodeRef.current && selectedNodeRef.current.id === nodeId) {
      const updatedNode = findNode(updatedNodes, nodeId);
      selectedNodeRef.current = updatedNode;
      setSelectedNode(updatedNode);
    }
    
    notifyChange(updatedNodes);
  };

  const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes < 1024) return `${bytes || 0} B`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const triggerImageUpload = () => {
    if (selectedNode?.type !== 'send_message' || uploadingNodeId) return;
    imageInputRef.current?.click();
  };

  const uploadMessageImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedNode || selectedNode.type !== 'send_message') return;

    const nodeId = selectedNode.id;
    setImageUploadError(null);
    setUploadingNodeId(nodeId);

    try {
      const payload = new FormData();
      payload.append('image', file);
      const response = await axios.post(`${API_BASE_URL}/campaign-assets`, payload, { timeout: 30000 });
      if (!response.data?.success || !response.data.asset) {
        throw new Error(response.data?.message || '图片上传失败');
      }
      updateNodeData(nodeId, { image_attachment: response.data.asset });
    } catch (error) {
      setImageUploadError({
        nodeId,
        message: error.response?.data?.message || error.message || '图片上传失败'
      });
    } finally {
      setUploadingNodeId(null);
    }
  };

  const removeMessageImage = () => {
    if (!selectedNode) return;
    setImageUploadError(null);
    updateNodeData(selectedNode.id, { image_attachment: null });
  };

  // Notify parent of changes
  const notifyChange = (updatedNodes) => {
    if (onChange) {
      onChange({ nodes: updatedNodes });
    }
  };

  // Render add menu
  const renderAddMenu = (parentId, branchType = 'child') => {
    const isOpen = showAddMenu?.nodeId === parentId && showAddMenu?.type === branchType;

    return (
      <div className="add-node-container">
        <button
          className={`add-node-btn ${isOpen ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowAddMenu(isOpen ? null : { nodeId: parentId, type: branchType });
          }}
        >
          +
        </button>
        
        {isOpen && (
          <div className="add-node-menu" onClick={(e) => e.stopPropagation()}>
            {actionTypes.map(actionType => (
              <button
                key={actionType.type}
                className="menu-item"
                onClick={(e) => {
                  e.stopPropagation();
                  addChildNode(parentId, branchType, actionType);
                }}
              >
                <span className="menu-icon">{actionType.icon}</span>
                <span className="menu-label">{actionType.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render node
  const renderNode = (node, isYesBranch = false, isNoBranch = false) => {
    if (!node) return null;

    const isCondition = node.type === 'connection_request_condition' || node.type === 'message_condition' || node.type === 'first_connection_condition';
    const isEnd = node.type === 'end';
    const isSelected = selectedNode?.id === node.id;

    return (
      <div className={`node-wrapper ${isYesBranch ? 'yes-branch' : ''} ${isNoBranch ? 'no-branch' : ''}`}>
        <div
          className={`workflow-node ${node.type} ${isSelected ? 'selected' : ''}`}
          onClick={() => setSelectedNode(node)}
        >
          <div className="node-icon">{node.icon}</div>
          <div className="node-label">{node.label}</div>
          
          {isCondition && (
            <div className="condition-badge">
              <span className="yes-label">YES</span>
              <span className="no-label">NO</span>
            </div>
          )}

          {node.id !== 'start' && (
            <button
              className="node-delete"
              onClick={(e) => {
                e.stopPropagation();
                deleteNode(node.id);
              }}
            >
              ✕
            </button>
          )}
          
          {isCondition && node.type === 'connection_request_condition' && (
            <button
              className="add-branch-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowAddMenu({ nodeId: node.id, type: 'branch' });
              }}
            >
              + Add Branch
            </button>
          )}
        </div>

        {/* Condition node branches */}
        {isCondition && (
          <div className="condition-branches">
            <div className="branch-container yes-branch-container">
              <div className="branch-label">YES</div>
              {node.yesChild ? (
                <>
                  <div className="connection-line vertical"></div>
                  {renderNode(node.yesChild, true, false)}
                </>
              ) : (
                renderAddMenu(node.id, 'yes')
              )}
            </div>

            <div className="branch-container no-branch-container">
              <div className="branch-label">NO</div>
              {node.noChild ? (
                <>
                  <div className="connection-line vertical"></div>
                  {renderNode(node.noChild, false, true)}
                </>
              ) : (
                renderAddMenu(node.id, 'no')
              )}
            </div>
          </div>
        )}

        {/* Regular node child */}
        {!isCondition && !isEnd && (
          <>
            {node.child ? (
              <>
                <div className="connection-line vertical"></div>
                {renderNode(node.child, false, false)}
              </>
            ) : (
              renderAddMenu(node.id, 'child')
            )}
          </>
        )}
      </div>
    );
  };

  // Zoom controls
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.3));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Pan controls
  const handleMouseDown = (e) => {
    if (e.target.closest('.workflow-node') || e.target.closest('.add-node-menu') || e.target.closest('.add-node-btn') || e.target.closest('.add-node-container')) {
      return; // Don't pan when clicking nodes or menus
    }
    setIsPanning(true);
    setStartPan({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;
    setPan({
      x: e.clientX - startPan.x,
      y: e.clientY - startPan.y
    });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  return (
    <div className="workflow-builder-v2">
      <input
        ref={imageInputRef}
        className="message-image-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={uploadMessageImage}
      />
      {/* Zoom Controls */}
      <div className="zoom-controls">
        <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In">
          <span>+</span>
        </button>
        <span className="zoom-level">{Math.round(zoom * 100)}%</span>
        <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out">
          <span>−</span>
        </button>
        <button className="zoom-btn reset" onClick={handleResetZoom} title="Reset View">
          <span>⟲</span>
        </button>
      </div>

      <div 
        className="workflow-canvas-v2"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
      >
        <div 
          className="workflow-tree"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0'
          }}
        >
          {renderNode(nodes[0])}
        </div>
      </div>

      {/* Configuration Panel */}
      {selectedNode && selectedNode.id !== 'start' && (
        <div className="node-config-panel">
          <div className="panel-header">
            <h3>{selectedNode.icon} {selectedNode.label}</h3>
            <button
              className="btn-close"
              onClick={() => setSelectedNode(null)}
            >
              ✕
            </button>
          </div>

          <div className="panel-content">
            {/* Connection Request & Message */}
            {(selectedNode.type === 'connection_request' || selectedNode.type === 'send_message') && (
              <>
                <div className="config-field">
                  <label>Message:</label>
                  <textarea
                    value={selectedNode.data.message || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { message: e.target.value })}
                    placeholder="Enter your message..."
                    rows="4"
                  />
                </div>
                <div className="config-field">
                  <label>Dynamic Fields:</label>
                  <div className="dynamic-fields">
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => insertFieldAtCursor('{FIRST_NAME}', selectedNode.id)}
                    >
                      First name
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => {
                        const currentMessage = selectedNode.data.message || '';
                        const cursorPos = document.activeElement?.selectionStart || currentMessage.length;
                        const newMessage = currentMessage.slice(0, cursorPos) + '{LAST_NAME}' + currentMessage.slice(cursorPos);
                        updateNodeData(selectedNode.id, { message: newMessage });
                      }}
                    >
                      Last name
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => {
                        const currentMessage = selectedNode.data.message || '';
                        const cursorPos = document.activeElement?.selectionStart || currentMessage.length;
                        const newMessage = currentMessage.slice(0, cursorPos) + '{FULL_NAME}' + currentMessage.slice(cursorPos);
                        updateNodeData(selectedNode.id, { message: newMessage });
                      }}
                    >
                      Full name
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => {
                        const currentMessage = selectedNode.data.message || '';
                        const cursorPos = document.activeElement?.selectionStart || currentMessage.length;
                        const newMessage = currentMessage.slice(0, cursorPos) + '{POSITION}' + currentMessage.slice(cursorPos);
                        updateNodeData(selectedNode.id, { message: newMessage });
                      }}
                    >
                      Position
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => {
                        const currentMessage = selectedNode.data.message || '';
                        const cursorPos = document.activeElement?.selectionStart || currentMessage.length;
                        const newMessage = currentMessage.slice(0, cursorPos) + '{COMPANY}' + currentMessage.slice(cursorPos);
                        updateNodeData(selectedNode.id, { message: newMessage });
                      }}
                    >
                      Company
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => {
                        const currentMessage = selectedNode.data.message || '';
                        const cursorPos = document.activeElement?.selectionStart || currentMessage.length;
                        const newMessage = currentMessage.slice(0, cursorPos) + '{LOCATION}' + currentMessage.slice(cursorPos);
                        updateNodeData(selectedNode.id, { message: newMessage });
                      }}
                    >
                      Location
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => {
                        const currentMessage = selectedNode.data.message || '';
                        const cursorPos = document.activeElement?.selectionStart || currentMessage.length;
                        const newMessage = currentMessage.slice(0, cursorPos) + '{HEADLINE}' + currentMessage.slice(cursorPos);
                        updateNodeData(selectedNode.id, { message: newMessage });
                      }}
                    >
                      Headline
                    </button>
                  </div>
                </div>

                {selectedNode.type === 'send_message' && (
                  <div className="config-field message-image-field">
                    <label>Image:</label>
                    {selectedNode.data.image_attachment?.id ? (
                      <div className="message-image-preview">
                        <img
                          src={`${API_BASE_URL}/campaign-assets/${encodeURIComponent(selectedNode.data.image_attachment.id)}/preview`}
                          alt={selectedNode.data.image_attachment.filename || 'Message attachment'}
                        />
                        <div className="message-image-details">
                          <span title={selectedNode.data.image_attachment.filename}>
                            {selectedNode.data.image_attachment.filename || 'Image attachment'}
                          </span>
                          <small>{formatFileSize(selectedNode.data.image_attachment.size)}</small>
                        </div>
                        <button
                          type="button"
                          className="message-image-remove"
                          onClick={removeMessageImage}
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="message-image-upload"
                        onClick={triggerImageUpload}
                        disabled={uploadingNodeId === selectedNode.id}
                      >
                        {uploadingNodeId === selectedNode.id ? 'Uploading...' : 'Upload image'}
                      </button>
                    )}
                    {imageUploadError?.nodeId === selectedNode.id && (
                      <div className="message-image-error">{imageUploadError.message}</div>
                    )}
                    <div className="message-image-hint">JPG, PNG, or WebP up to 10 MB</div>
                  </div>
                )}
              </>
            )}

            {/* InMail */}
            {selectedNode.type === 'inmail' && (
              <>
                <div className="config-field">
                  <label>Subject:</label>
                  <input
                    type="text"
                    value={selectedNode.data.subject || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { subject: e.target.value })}
                    placeholder="Email subject..."
                  />
                </div>
                <div className="config-field">
                  <label>Message:</label>
                  <textarea
                    value={selectedNode.data.message || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { message: e.target.value })}
                    placeholder="Enter your InMail message..."
                    rows="4"
                  />
                </div>
                <div className="config-field">
                  <label>Dynamic Fields:</label>
                  <div className="dynamic-fields">
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => insertFieldAtCursor('{FIRST_NAME}', selectedNode.id)}
                    >
                      First name
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => insertFieldAtCursor('{LAST_NAME}', selectedNode.id)}
                    >
                      Last name
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => insertFieldAtCursor('{FULL_NAME}', selectedNode.id)}
                    >
                      Full name
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => insertFieldAtCursor('{POSITION}', selectedNode.id)}
                    >
                      Position
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => insertFieldAtCursor('{COMPANY}', selectedNode.id)}
                    >
                      Company
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => insertFieldAtCursor('{LOCATION}', selectedNode.id)}
                    >
                      Location
                    </button>
                    <button
                      type="button"
                      className="dynamic-field-btn"
                      onClick={() => insertFieldAtCursor('{HEADLINE}', selectedNode.id)}
                    >
                      Headline
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Delay */}
            {selectedNode.type === 'delay' && (
              <div className="config-field">
                <label>Delay Duration (hours):</label>
                <input
                  type="number"
                  min="1"
                  value={selectedNode.data.hours || 24}
                  onChange={(e) => {
                    const hours = parseInt(e.target.value) || 24;
                    updateNodeData(selectedNode.id, { hours });
                  }}
                />
                <div className="quick-delay-buttons">
                  <button onClick={() => updateNodeData(selectedNode.id, { hours: 1 })}>1 Hour</button>
                  <button onClick={() => updateNodeData(selectedNode.id, { hours: 3 })}>3 Hours</button>
                  <button onClick={() => updateNodeData(selectedNode.id, { hours: 24 })}>1 Day</button>
                  <button onClick={() => updateNodeData(selectedNode.id, { hours: 96 })}>4 Days</button>
                </div>
              </div>
            )}

            {/* Condition */}
            {(selectedNode.type === 'connection_request_condition' || selectedNode.type === 'message_condition' || selectedNode.type === 'first_connection_condition') && (
              <div className="config-field">
                <p className="hint">
                  This is a conditional node. The workflow will split into two paths based on the condition result.
                </p>
                {selectedNode.type === 'first_connection_condition' ? (
                  <ul className="condition-info">
                    <li><strong>YES path:</strong> Executes if target is a first-degree connection</li>
                    <li><strong>NO path:</strong> Executes if target is not a first-degree connection</li>
                    <li><strong>Note:</strong> This check is performed immediately (no timeout needed)</li>
                  </ul>
                ) : (
                  <ul className="condition-info">
                    <li><strong>YES path:</strong> Executes immediately when condition is met (e.g., connection accepted)</li>
                    <li><strong>NO path:</strong> Executes after timeout if condition is not met</li>
                  </ul>
                )}
                
                {selectedNode.type !== 'first_connection_condition' && (
                <div className="config-field">
                  <label>⏱️ Timeout for NO Path (等待多久后检查)</label>
                  <p className="hint-small">如果在此时间内未满足条件，将执行NO分支</p>
                  
                  <div className="timeout-input-group" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="number"
                      min="1"
                      max="999"
                      className="timeout-number-input"
                      value={selectedNode.data?.timeoutValue || 4}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        const value = parseInt(e.target.value) || 1;
                        const unit = selectedNode.data?.timeoutUnit || 'days';
                        const hours = unit === 'days' ? value * 24 : value;
                        updateNodeData(selectedNode.id, { 
                          timeoutValue: value,
                          timeoutUnit: unit,
                          timeoutHours: hours
                        });
                      }}
                      placeholder="输入数字"
                    />
                    <select
                      className="timeout-unit-select"
                      value={selectedNode.data?.timeoutUnit || 'days'}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        const unit = e.target.value;
                        const value = selectedNode.data?.timeoutValue || 4;
                        const hours = unit === 'days' ? value * 24 : value;
                        updateNodeData(selectedNode.id, { 
                          timeoutUnit: unit,
                          timeoutValue: value,
                          timeoutHours: hours
                        });
                      }}
                    >
                      <option value="hours">Hours (小时)</option>
                      <option value="days">Days (天)</option>
                    </select>
                  </div>

                  <div className="quick-time-buttons" onClick={(e) => e.stopPropagation()}>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      updateNodeData(selectedNode.id, { timeoutValue: 1, timeoutUnit: 'days', timeoutHours: 24 });
                    }}>1 Day</button>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      updateNodeData(selectedNode.id, { timeoutValue: 3, timeoutUnit: 'days', timeoutHours: 72 });
                    }}>3 Days</button>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      updateNodeData(selectedNode.id, { timeoutValue: 4, timeoutUnit: 'days', timeoutHours: 96 });
                    }}>4 Days</button>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      updateNodeData(selectedNode.id, { timeoutValue: 7, timeoutUnit: 'days', timeoutHours: 168 });
                    }}>7 Days</button>
                  </div>
                </div>
                )}

                <div className="info-box" style={{marginTop: '16px'}}>
                  <p><strong>工作原理：</strong></p>
                  {selectedNode.type === 'first_connection_condition' ? (
                    <ul>
                      <li>✅ <strong>YES</strong>: 如果target是一度人脉，<strong>立即</strong>执行YES分支</li>
                      <li>❌ <strong>NO</strong>: 如果target不是一度人脉，<strong>立即</strong>执行NO分支</li>
                      <li>⚡ 此检查是<strong>立即执行</strong>的，不需要等待超时</li>
                    </ul>
                  ) : (
                    <ul>
                      <li>✅ <strong>YES</strong>: 一旦条件满足（如connection被接受），<strong>立即</strong>执行YES分支</li>
                      <li>⏱️ <strong>NO</strong>: 如果在 {selectedNode.data?.timeoutValue || 4} {selectedNode.data?.timeoutUnit === 'hours' ? '小时' : '天'}内条件未满足，执行NO分支</li>
                    </ul>
                  )}
                  <p style={{marginTop: '8px', fontSize: '12px', color: '#666'}}>
                    示例：发送connection request后，如果对方接受了就立即发消息；如果{selectedNode.data?.timeoutValue || 4}{selectedNode.data?.timeoutUnit === 'hours' ? '小时' : '天'}后还没接受，就结束workflow。
                  </p>
                </div>
              </div>
            )}

            {/* End Node */}
            {selectedNode.type === 'end' && (
              <div className="config-field">
                <p className="hint">
                  ✓ This is an <strong>End node</strong>. The workflow for this target will terminate here.
                </p>
                <div className="info-box">
                  <p><strong>What happens:</strong></p>
                  <ul>
                    <li>No further actions will be executed for this target</li>
                    <li>The target status will be marked as "completed"</li>
                    <li>Campaign statistics will be updated</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className="panel-footer">
            <button
              className="btn-delete"
              onClick={() => {
                deleteNode(selectedNode.id);
                setSelectedNode(null);
              }}
            >
              Delete Node
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkflowBuilder;
